import { useId } from "react";
import { getUnitName } from "../../utils/unitNames";
import "./ItemConsumptionFrequencies.css";

export const consumptionFrequencyDraft = (records = []) => Object.fromEntries(records.map((record) => [String(record.location_id), {
  quantity: record.quantity == null ? "" : String(record.quantity),
  interval_days: record.interval_days == null ? "" : String(record.interval_days),
  unit_id: record.unit_id == null ? "" : String(record.unit_id),
}]));

export function consumptionUnits(form, units = []) {
  const ids = [...new Set([form.unit_id, form.subunit_id, form.subunit_id && form.subunit2_id].filter(Boolean).map(String))];
  return ids.map((id) => units.find((unit) => String(unit.id) === id)).filter(Boolean);
}

const hasValue = (value) => String(value ?? "").trim() !== "";
const hasFrequency = (row) => hasValue(row?.quantity) || hasValue(row?.interval_days);
const normalizeQuantity = (value) => {
  const text = String(value ?? "").trim();
  if (!/^(?:\d+(?:\.\d{0,8})?|\.\d{1,8})$/.test(text)) return null;
  const [whole, fractional = ""] = text.split(".");
  const integer = whole.replace(/^0+/, "") || "0";
  const decimals = fractional.replace(/0+$/, "");
  if (integer.length > 12 || (integer === "0" && !decimals)) return null;
  return `${integer}${decimals ? `.${decimals}` : ""}`;
};

export function serializeConsumptionFrequencies(draft, locations, units, baseUnitId, t = (text) => text) {
  const unitIds = new Set(units.map((unit) => Number(unit.id)));
  const locationIds = new Set(locations.map((location) => Number(location.id)));
  const payload = [];
  for (const [locationId, row] of Object.entries(draft)) {
    if (!hasFrequency(row)) continue;
    const location = locations.find((entry) => Number(entry.id) === Number(locationId));
    const error = (message) => new Error(`${location?.name || t("Location")}: ${t(message)}`);
    if (!locationIds.has(Number(locationId))) throw error("This location is unavailable. Reload the item before saving.");
    const quantity = normalizeQuantity(row.quantity);
    if (!quantity) throw error("Enter a quantity greater than zero, with up to twelve integer digits and eight decimal places.");
    const days = Number(row.interval_days);
    if (!Number.isSafeInteger(days) || days < 1 || days > 36500) throw error("Enter a whole number of days between 1 and 36500.");
    const unitId = Number(row.unit_id || baseUnitId);
    if (!unitIds.has(unitId)) throw error("Choose one of the item's current units for this frequency.");
    payload.push({ location_id: Number(locationId), quantity, interval_days: days, unit_id: unitId });
  }
  return payload;
}

export default function ItemConsumptionFrequencies({ locations = [], value = {}, onChange, units = [], baseUnitId, languageId, t, disabled = false, unavailable = false, onRetry, retrying = false, collapsible = false }) {
  const id = useId();
  const Container = collapsible ? "details" : "section";
  const update = (locationId, changes) => onChange({ ...value, [locationId]: { quantity: "", interval_days: "", unit_id: String(baseUnitId || ""), ...value[locationId], ...changes } });
  return <Container className={`icf-section${collapsible ? " aip-disclosure" : ""}`} aria-label={t("Consumption frequency")}>
    {collapsible && <summary><h4>{t("Consumption frequency")}</h4></summary>}
    <header>{!collapsible && <h4>{t("Consumption frequency")}</h4>}<p>{t("Set the usual consumption for each location. Leave a row blank if it is not defined.")}</p></header>
    {unavailable ? <div role="alert"><p>{t("Could not load consumption locations. Reload before saving frequencies.")}</p>{onRetry && <button type="button" className="icf-clear" onClick={onRetry} disabled={retrying || disabled}>{t(retrying ? "Loading..." : "Retry")}</button>}</div> : !locations.length ? <p role="status">{t("No locations are available for consumption frequencies.")}</p> :
      <div className="icf-locations">{locations.map((location) => {
        const row = value[location.id] || {};
        const unitId = row.unit_id || baseUnitId || "";
        const invalidUnit = Boolean(unitId) && !units.some((unit) => Number(unit.id) === Number(unitId));
        return <div className="icf-location" key={location.id}>
          <div className="icf-locationName"><strong>{location.name || location.code}</strong>{location.code && <small>{location.code}</small>}</div>
          <div className="icf-frequency">
            <span>{t("We consume")}</span>
            <input id={`${id}-${location.id}-quantity`} type="number" min="0.00000001" step="0.00000001" inputMode="decimal" value={row.quantity || ""}
              aria-label={`${t("Consumption quantity")} · ${location.name}`} disabled={disabled || unavailable} onChange={(event) => update(location.id, { quantity: event.target.value })} />
            <select id={`${id}-${location.id}-unit`} value={invalidUnit ? "" : String(unitId)} aria-invalid={invalidUnit || undefined}
              aria-label={`${t("Consumption unit")} · ${location.name}`} disabled={disabled || unavailable || !units.length} onChange={(event) => update(location.id, { unit_id: event.target.value })}>
              {(!unitId || invalidUnit) && <option value="" disabled>{t("Select unit")}</option>}
              {units.map((unit) => <option key={unit.id} value={unit.id}>{getUnitName(unit, languageId)}</option>)}
            </select>
            <span>{t("every")}</span>
            <input id={`${id}-${location.id}-days`} type="number" min="1" max="36500" step="1" inputMode="numeric" value={row.interval_days || ""}
              aria-label={`${t("Consumption interval in days")} · ${location.name}`} disabled={disabled || unavailable} onChange={(event) => update(location.id, { interval_days: event.target.value })} />
            <span>{t("days")}</span>
          </div>
          <button type="button" className="icf-clear" aria-label={`${t("Remove consumption frequency")} · ${location.name}`} disabled={disabled || !hasFrequency(row)} onClick={() => {
            const next = { ...value }; delete next[location.id]; onChange(next);
          }}>{t("Clear")}</button>
          {invalidUnit && hasFrequency(row) && <p className="icf-warning" role="alert">{t("Choose one of the item's current units for this frequency.")}</p>}
        </div>;
      })}</div>}
  </Container>;
}
